// frontend/src/components/FinanceReport/Accounting/components/accounts/suppliers/SuppliersTab.jsx

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAllSupplierPrepayments,
  getAllSuppliers,
} from "./requests";

export default function SuppliersTab() {
  const navigate = useNavigate();

  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [list, suppliersList] = await Promise.all([
        getAllSupplierPrepayments(),
        getAllSuppliers().catch(() => []),
      ]);
      setItems(list);
      setSuppliers(suppliersList);
    } catch (err) {
      setError(err.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Баланс поставщика в юанях (income PAID − expense PAID)
  const balanceOf = (supplierId) => {
    let bal = 0;
    items.forEach((p) => {
      if (String(p.supplier) !== String(supplierId)) return;
      if (p.payment_status !== "PAID") return;
      const amt = Number(p.amount || 0);
      if (p.type === "income") bal += amt;
      else bal -= amt;
    });
    return bal;
  };

  // Список поставщиков с балансом — объединяем тех, кто есть в /suppliers/,
  // и тех, кто встречается только в движениях
  const supplierRows = useMemo(() => {
    const byId = new Map();

    suppliers.forEach((s) => {
      byId.set(String(s.id), {
        id: s.id,
        name: s.name || `Поставщик #${s.id}`,
      });
    });

    items.forEach((p) => {
      const key = String(p.supplier);
      if (!byId.has(key)) {
        byId.set(key, {
          id: p.supplier,
          name: p.supplier_name || `Поставщик #${p.supplier}`,
        });
      }
    });

    return Array.from(byId.values())
      .map((s) => ({ ...s, balance: balanceOf(s.id) }))
      .sort((a, b) => a.name.localeCompare(b.name, "ru"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppliers, items]);

  const totalBalance = useMemo(
    () => supplierRows.reduce((sum, s) => sum + s.balance, 0),
    [supplierRows]
  );

  if (loading) return <div className="p-4 text-slate-500">Загрузка…</div>;

  return (
    <>
      <div className="mb-4">
        <h2 className="text-2xl font-bold">Счета поставщиков</h2>
        <p className="mt-1 text-sm text-slate-500">
          Нажмите на поставщика, чтобы открыть движение по его счёту.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="p-4 border rounded-xl mb-6 bg-gray-50">
        <p className="text-sm text-gray-500">Общий баланс</p>
        <p className="text-2xl font-bold text-black">
          ¥ {totalBalance.toLocaleString("ru-RU", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </p>
      </div>

      <div className="space-y-4">
        {supplierRows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
            <div className="text-3xl">🏭</div>
            <p className="mt-2 font-semibold text-slate-800">Поставщиков пока нет</p>
            <p className="mt-1 text-sm text-slate-500">
              Поставщик появится здесь, когда будет первое движение.
            </p>
          </div>
        ) : (
          supplierRows.map((s) => (
            <div
              key={s.id}
              onClick={() =>
                navigate(`/accounting/supplier-accounts/${s.id}`)
              }
              className="flex justify-between items-start p-4 rounded-xl border bg-white cursor-pointer transition hover:shadow-sm"
            >
              <div>
                <p className="font-semibold text-lg">{s.name}</p>
                <p
                  className={`text-lg font-medium ${
                    s.balance >= 0 ? "text-green-700" : "text-red-700"
                  }`}
                >
                  ¥{" "}
                  {s.balance.toLocaleString("ru-RU", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}